import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  billingService,
  formatAmount,
  type BillingPlan,
} from '@/services/billing'
import { toast } from 'sonner'
import { Loader2, Plus } from 'lucide-react'

interface ManagePlansDrawerProps {
  onChanged?: () => void
}

/**
 * List + create subscription plans. Plan terms (amount/interval) are immutable
 * once a subscription references them (backend-enforced) — pricing changes are
 * a new plan + deactivating the old one.
 */
export function ManagePlansDrawer({ onChanged }: ManagePlansDrawerProps) {
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [amount, setAmount] = useState('')
  const [interval, setInterval] = useState<'month' | 'year'>('month')

  const fetchPlans = async () => {
    try {
      setLoading(true)
      setPlans(await billingService.listPlans(true))
    } catch (err) {
      console.error('Failed to load plans:', err)
      toast.error('Failed to load plans')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPlans()
  }, [])

  const handleCreate = async () => {
    const amountNum = Number(amount)
    if (!name.trim() || !code.trim()) {
      toast.error('Plan name and code are required')
      return
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toast.error('Enter a valid amount in dollars')
      return
    }
    setSaving(true)
    try {
      await billingService.createPlan({
        code: code.trim(),
        name: name.trim(),
        amount_cents: Math.round(amountNum * 100),
        interval,
      })
      toast.success(`Plan "${name}" created`)
      setName('')
      setCode('')
      setAmount('')
      fetchPlans()
      onChanged?.()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail
      toast.error(detail || 'Failed to create plan')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (plan: BillingPlan) => {
    try {
      await billingService.updatePlan(plan.id, { is_active: !plan.is_active })
      toast.success(`Plan "${plan.name}" ${plan.is_active ? 'deactivated' : 'activated'}`)
      fetchPlans()
      onChanged?.()
    } catch (err) {
      console.error('Failed to update plan:', err)
      toast.error('Failed to update plan')
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Create a plan</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="plan-name">Name</Label>
            <Input
              id="plan-name"
              placeholder="e.g. Pro"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="plan-code">Code</Label>
            <Input
              id="plan-code"
              placeholder="e.g. pro-monthly"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="plan-amount">Amount (USD)</Label>
            <Input
              id="plan-amount"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 99.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Billing interval</Label>
            <Select value={interval} onValueChange={(v) => setInterval(v as 'month' | 'year')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleCreate} disabled={saving} size="sm">
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Create Plan
        </Button>
        <p className="text-xs text-muted-foreground">
          Pricing is locked once a plan has subscribers — to change pricing, create a
          new plan and deactivate this one.
        </p>
      </div>

      <Separator />

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Existing plans</h3>
        {loading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No plans yet.</p>
        ) : (
          <div className="space-y-2">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {plan.name}
                    {!plan.is_active && <Badge variant="outline">Inactive</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {plan.code} — {formatAmount(plan.amount_cents, plan.currency)}/
                    {plan.interval}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleActive(plan)}
                >
                  {plan.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
